import {
  Button,
  Center,
  CloseButton,
  Drawer,
  Group,
  Loader,
  Text,
} from "@mantine/core"
import { modals } from "@mantine/modals"
import { useCallback, useRef } from "react"
import { Trans, useTranslation } from "react-i18next"
import { useCrudItem } from "../../../hooks/useCrud"
import { useUser } from "../../user/hooks/useUser"
import { useDeleteTag } from "../hooks/useTags"
import type { Tag } from "../models/tag"
import { TagSchema } from "../models/tag"
import TagDetailContent from "./TagDetailContent"
import TagFormContent from "./TagFormContent"

export type DrawerMode = "view" | "create" | "edit"

interface Props {
  opened: boolean
  onClose: () => void
  mode: DrawerMode
  selectedId: number | null
  onDeleted: () => void
  onSwitchToEdit: () => void
}

export default function TagDrawer({
  opened,
  onClose,
  mode,
  selectedId,
  onDeleted,
  onSwitchToEdit,
}: Props) {
  const { t } = useTranslation()
  const { data: tag, isLoading } = useCrudItem<Tag>(
    "tags",
    "post-tag",
    TagSchema,
    mode !== "create" ? selectedId : null,
  )
  const { data: userData } = useUser()
  const canManage = userData?.can?.viewMenuAdmin ?? false
  const deleteMutation = useDeleteTag()
  const formRef = useRef<{ submit: () => void }>(null)
  const isFormMode = mode === "edit" || mode === "create"

  const handleDelete = useCallback(() => {
    if (!tag?.id) return
    modals.openConfirmModal({
      title: t("tags.confirmDelete"),
      children: <Text size="sm">{t("tags.confirmDeleteMessage")}</Text>,
      labels: { confirm: t("common.delete"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => {
        deleteMutation.mutate(tag.id, { onSuccess: () => onDeleted() })
      },
    })
  }, [tag, deleteMutation, onDeleted, t])

  const title =
    mode === "create"
      ? t("tags.createTitle")
      : mode === "edit"
        ? t("tags.editTitle")
        : (tag?.name ?? "")

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      withCloseButton={false}
    >
      <Drawer.Header>
        <Drawer.Title>
          <Text fw={600} size="md" lineClamp={1}>
            {title}
          </Text>
        </Drawer.Title>
        <Group gap="xs">
          {mode === "view" && canManage && (
            <>
              <Button
                size="xs"
                variant="default"
                onClick={onSwitchToEdit}
                leftSection={<i className="fa fa-pencil" />}
              >
                <Trans i18nKey="common.edit" />
              </Button>
              <Button
                size="xs"
                color="red"
                onClick={handleDelete}
                leftSection={<i className="fa fa-trash" />}
              >
                <Trans i18nKey="common.delete" />
              </Button>
            </>
          )}
          {isFormMode && (
            <Button size="xs" onClick={() => formRef.current?.submit()}>
              <Trans
                i18nKey={mode === "edit" ? "common.update" : "common.create"}
              />
            </Button>
          )}
          <CloseButton onClick={onClose} />
        </Group>
      </Drawer.Header>

      {mode === "create" ? (
        <TagFormContent ref={formRef} onSuccess={onClose} />
      ) : isLoading ? (
        <Center h="100%">
          <Loader />
        </Center>
      ) : !tag ? (
        <Text p="md">
          <Trans i18nKey="common.notFound" />
        </Text>
      ) : mode === "edit" ? (
        <TagFormContent ref={formRef} tag={tag} onSuccess={onClose} />
      ) : (
        <TagDetailContent tag={tag} />
      )}
    </Drawer>
  )
}
